/* eslint-disable max-lines */

import type { IRiskStatus, IRiskTreatment } from '@/types/risk'
import { RISK_LEVELS as RISK_LEVELS_CONSTANTS } from '@/lib/constants/risks'
export const RISK_TABLE = {
  TITLE: 'Gestor de Riesgos',
  CREATE: 'Nuevo Riesgo',
  SEARCH_PLACEHOLDER: 'Buscar riesgos...',
  REVIEWED_ON: (date: string) => `Revisado ${date}`,
  BULK_UPLOAD: 'Importar CSV',
  EXPORT: {
    BUTTON: 'Exportar CSV',
    EMPTY: 'No hay riesgos que exportar con los filtros actuales.',
  },
  METHODOLOGY: {
    LABEL: 'Metodología',
    SIMPLE: 'Simple',
    ADVANCED: 'Avanzada',
    ADVANCED_LOCKED: 'Mejora tu plan para desbloquear el gestor de riesgos avanzado',
    BANNER_DESCRIPTION:
      'La metodología Avanzada añade la capa ISO 27005 a cada riesgo: riesgos listos para usar desde el catálogo, y amenazas y vulnerabilidades sugeridas.',
    SWITCH_TO_ADVANCED: 'Cambiar a Avanzada',
    REQUEST_UPGRADE: 'Solicitar mejora',
    UPGRADE_EMAIL_SUBJECT: 'Gestor de riesgos avanzado',
  },
}
export const RISK_BULK_UPLOAD_DIALOG = {
  TITLE: 'Importar Riesgos desde CSV',
  DESCRIPTION:
    'Sube un archivo CSV para importar riesgos de forma masiva. Todas las filas deben ser válidas — cualquier error se mostrará a continuación y no se creará nada hasta que los corrijas.',
  UPLOAD_FILE: 'Subir CSV',
  UPLOADING: 'Subiendo...',
  CREATING: 'Creando riesgos...',
  SUCCESS: 'Riesgos importados correctamente',
  CANCEL: 'Cancelar',
  VALIDATION_TABLE_ROW: 'Fila',
  VALIDATION_TABLE_FIELD: 'Campo',
  VALIDATION_TABLE_ERROR: 'Error',
  VALIDATION_TABLE_RECEIVED_VALUE: 'Valor recibido',
  DOWNLOAD_SAMPLE: 'Descargar CSV de ejemplo',
  HEADER_ERROR_TITLE: 'La cabecera de tu CSV tiene problemas',
  HEADER_ERROR_DESCRIPTION:
    'Corrige la fila de cabecera en tu archivo y vuelve a subirlo. Las columnas aceptadas se muestran a continuación.',
  ACCEPTED_COLUMNS_REQUIRED: 'Columnas obligatorias',
  ACCEPTED_COLUMNS_OPTIONAL: 'Columnas opcionales',
  SUMMARY_LINE:
    '{totalRows} filas revisadas, {errorCount} errores encontrados. Corrígelos en tu CSV y vuelve a subirlo.',
  ERROR_UNKNOWN_COLUMN: 'Columna desconocida: {field}',
  ERROR_MISSING_REQUIRED_COLUMN: 'Falta la columna obligatoria: {field}',
  ERROR_MALFORMED_ROW: 'Fila mal formada',
  ERROR_REQUIRED: '{field} es obligatorio',
  ERROR_INVALID_INTEGER_RANGE:
    '{field} debe ser un número entero dentro del rango permitido. Recibido: "{receivedValue}"',
  ERROR_INVALID_DATE: 'Fecha no válida en {field}. Recibido: "{receivedValue}"',
  ERROR_INVALID_TREATMENT_TYPE: 'Tipo de tratamiento no válido "{receivedValue}". Valores permitidos: {allowedValues}',
  ERROR_INVALID_CLUSTER: 'Cluster no válido "{receivedValue}". Valores permitidos: {allowedValues}',
  ERROR_INVALID_ASSET: 'Activo no válido "{receivedValue}". Valores permitidos: {allowedValues}',
  ERROR_INVALID_INTERESTED_PARTY: 'Parte interesada no válida "{receivedValue}". Valores permitidos: {allowedValues}',
  ERROR_OWNER_NOT_FOUND: 'Usuario no encontrado en esta organización: "{receivedValue}"',
  ERROR_DUPLICATE_IN_FILE: 'Nombre duplicado en este archivo: "{receivedValue}"',
  ERROR_DUPLICATE_IN_DB: 'Ya existe un riesgo con este nombre: "{receivedValue}"',
  ERROR_TREATMENT_CONTROL_NOT_FOUND: 'Control de tratamiento no encontrado: "{receivedValue}"',
  ERROR_INVALID_STATUS: 'Estado no válido "{receivedValue}". Valores permitidos: {allowedValues}',
  ERROR_INVALID_THREAT: 'Amenaza no válida "{receivedValue}". Valores permitidos: {allowedValues}',
  ERROR_INVALID_VULNERABILITY: 'Vulnerabilidad no válida "{receivedValue}". Valores permitidos: {allowedValues}',
  ERROR_INVALID_IMPACT_DIMENSION:
    'Dimensión de impacto no válida "{receivedValue}". Valores permitidos: {allowedValues}',
  ERROR_AFFECTED_ASSET_NOT_FOUND: 'Activo no encontrado en tu inventario: "{receivedValue}"',
  ERROR_OPERATIONAL_ERROR: 'No se pueden crear los riesgos en este momento. Inténtalo de nuevo.',
}
export const RISK_FILTERS = {
  ALL: 'Todos',
  FILTER_BY_OWNER: 'Filtrar por responsable',
  FILTER_BY_CLUSTER: 'Filtrar por dominio de riesgo',
  FILTER_BY_ASSET: 'Filtrar por activo',
  FILTER_BY_INTERESTED_PARTY: 'Filtrar por parte interesada',
  FILTER_BY_TREATMENT: 'Filtrar por tratamiento',
  FILTER_BY_CONTROL: 'Filtrar por salvaguarda',
  FILTER_BY_INHERENT_RISK: 'Filtrar por riesgo inherente',
  FILTER_BY_RESIDUAL_RISK: 'Filtrar por riesgo residual',
}

export const RISK_BULK_ACTIONS = {
  SELECTED: 'seleccionados',
  UPDATING: 'Actualizando...',
  CLEAR: 'Limpiar',
  DELETE: 'Eliminar',
  CANCEL: 'Cancelar',
  CONFIRM_DELETE_TITLE: 'Eliminar riesgos',
  CONFIRM_DELETE_DESCRIPTION: (count: number) =>
    `¿Seguro que quieres eliminar ${count} ${count === 1 ? 'riesgo' : 'riesgos'}? Esta acción no se puede deshacer.`,
}

export const RISK_DRAWER = {
  RISK_DETAILS: 'Detalles del Riesgo',
  ATTRIBUTES: 'Atributos',
  RISK: 'Riesgo',
  NOTES: 'Notas',
  NO_NOTES: 'Sin notas',
  THREATS_AND_VULNERABILITIES: 'Amenazas y vulnerabilidades',
  CREATED: 'Creado',
  CLOSED: 'Cerrado',
  NOT_SET: 'Sin fecha',
  RISK_DOMAIN: 'Dominio de riesgo',
  NO_RISK_DOMAIN: 'Sin dominio de riesgo',
  INHERENT_RISK: 'Riesgo inherente',
  INHERENT_RISK_INFO: 'Antes de aplicar ninguna medida',
  TREATMENT: 'Tratamiento',
  SAFEGUARDS: 'Salvaguardas',
  NO_SAFEGUARD: 'Sin salvaguarda',
  SELECT_SAFEGUARDS: 'Seleccionar salvaguardas...',
  EFFECTIVENESS: 'Efectividad',
  RESIDUAL_RISK: 'Riesgo residual',
  RESIDUAL_RISK_INFO: 'Lo que queda tras las medidas anteriores',
  REACH: 'Alcance',
  REACH_PLACEHOLDER: 'A quién o a qué podría afectar este riesgo...',
  REACH_INFO: 'Procesos, sedes y sistemas a los que se extiende',
}

export const RISK_HISTORY = {
  TITLE: 'Historial de cambios',
  CREATED: 'Riesgo registrado',
  NO_CHANGES: 'Sin cambios',
  EMPTY_VALUE: 'Ninguno',
  UNKNOWN_USER: 'Sistema',
  FETCH_ERROR: 'No se pudo cargar el historial de cambios',
  FIELDS: {
    displayId: 'ID del riesgo',
    name: 'Nombre',
    status: 'Estado',
    owner: 'Responsable',
    inherentLikelihood: 'Probabilidad inherente',
    inherentImpact: 'Consecuencia inherente',
    inherentRiskLevel: 'Riesgo inherente',
    residualLikelihood: 'Probabilidad residual',
    residualImpact: 'Consecuencia residual',
    residualRiskLevel: 'Riesgo residual',
    effectiveness: 'Efectividad',
    treatmentType: 'Tratamiento',
    treatmentNotes: 'Medidas',
    controls: 'Salvaguardas',
    clusters: 'Dominio de riesgo',
    assets: 'Activos',
    affectedAssets: 'Activos afectados',
    threats: 'Amenazas',
    vulnerabilities: 'Vulnerabilidades',
    impactDimensions: 'Dimensiones de impacto',
    interestedParties: 'Partes interesadas',
    reach: 'Alcance',
    notes: 'Notas',
    createdAt: 'Fecha de creación',
    closedDate: 'Fecha de cierre',
  },
}

export const RISK_CREATION_DIALOG = {
  CREATE: 'Crear Riesgo',
  NAME: 'Nombre',
  NOTES: 'Notas',
  NO_NOTES: 'Sin notas',
  CATEGORIES: 'Categorías',
  BULK_UPLOAD: 'Carga Masiva',
  BULK_UPLOAD_TEMPLATE: 'Plantilla de Carga Masiva',
}

export const RISK_DETAILS = {
  DESCRIPTION: 'Descripción',
  OWNER: 'Responsable',
  STATUS: 'Estado',
  TREATMENT: 'Tratamiento',
  INHERENT: 'Riesgo Inherente',
  RESIDUAL: 'Riesgo Residual',
  RELATED_CONTROLS: 'Controles Relacionados',
  MISSING_RISK_DESCRIPTION: 'Falta descripción del riesgo',
  MISSING_NOTES: 'Faltan notas',
  LIKELIHOOD: 'Probabilidad',
  IMPACT: 'Consecuencia',
  SEVERITY: 'Severidad',
  NOTES: 'Notas',
  CATEGORIES: 'Categorías',
  TREATMENT_NOTES: 'Medida Potencial',
  TREATMENT_CONTROLS: 'Controles Asociados',
  TREATMENT_TYPE: 'Tipo',
  CREATED_AT: 'Creado el',
  EFFECTIVENESS: 'Efectividad',
  DELETED: 'Riesgo eliminado',
}

export const RISK_DELETE_DIALOG = {
  TITLE: 'Eliminar Riesgo',
  QUESTION: '¿Estás seguro de que quieres eliminar este riesgo?',
}

export const RISK_STATUS: { [K in IRiskStatus]: string } = {
  inProgress: 'En Curso',
  planned: 'Planificado',
  delayed: 'Retrasado',
  closed: 'Cerrado',
}

export const RISK_TREATMENT_LABELS: { [K in IRiskTreatment['type']]: string } = {
  mitigate: 'Mitigar',
  accept: 'Aceptar',
}

export const RISK_TREATMENT: { [K in IRiskTreatment['type']]: string } = {
  mitigate: 'Mitigar',
  accept: 'Aceptar',
}

export const RISK_TREATMENT_DESCRIPTIONS: { [K in IRiskTreatment['type']]: string } = {
  mitigate: 'Identificar controles a implementar o tareas a realizar que reducirán el nivel de riesgo.',
  accept: 'Decidir convivir con el riesgo sin tomar acciones adicionales.',
}

export const RISK_ASSESSMENT = {
  TITLE: 'Evaluación de Riesgos',
  ADD_CONTROL: 'Agregar Control',
  CONTROL_TABLE_HEADER: 'Control',
  UPDATING_RISK: 'Actualizando riesgo...',
  UPDATED_RISK: 'Riesgo actualizado',
  LAST_REVIEW_DATE: 'Última Fecha de Revisión',
}

export const RISK_ASSESSMENT_TABLE = {
  TABLE_HEADER: {
    NAME: 'Nombre',
    DESCRIPTION: 'Descripción',
    OWNER: 'Responsable',
    STATUS: 'Estado',
    TREATMENT: 'Tratamiento',
    INHERENT_RISK: 'Riesgo Inherente',
  },
}

export const RISK_ASSESSMENT_STATUS = {
  planned: 'Planificado',
  delayed: 'Retrasado',
  closed: 'Cerrado',
  inProgress: 'En Curso',
}

export const RISK_LEVELS = {
  [RISK_LEVELS_CONSTANTS.VERY_LOW]: 'Muy Bajo',
  [RISK_LEVELS_CONSTANTS.LOW]: 'Bajo',
  [RISK_LEVELS_CONSTANTS.MEDIUM]: 'Medio',
  [RISK_LEVELS_CONSTANTS.HIGH]: 'Alto',
  [RISK_LEVELS_CONSTANTS.VERY_HIGH]: 'Muy Alto',
  [RISK_LEVELS_CONSTANTS.UNKNOWN]: 'Desconocido',
}

export const RISK_CONTROLS_TABLE = {
  CONTROLS: 'Controles',
  STANDARDS: 'Estándares',
  ACTIONS: 'Acciones',
}

export const RISK_LEVEL_SELECTOR = {
  LIKELIHOOD: 'Probabilidad',
  IMPACT: 'Impacto',
  APPLY: 'Aplicar',
  RISK_LEVEL: 'Nivel de Riesgo',
}

export const RISK_FORM = {
  MEASURES: 'Medidas',
  MEASURES_PLACEHOLDER: 'Describe las medidas para mitigar este riesgo...',
  CONTROLS: 'Salvaguardas',
  EFFECTIVENESS: 'Efectividad',
  CANCEL: 'Cancelar',
  DELETE: 'Eliminar',
}

export const RISK_MESSAGES = {
  CREATING: 'Creando riesgo...',
  CREATED: (name: string) => `Riesgo "${name}" creado`,
  DELETED: 'Riesgo eliminado',
  BULK_EDIT_OWNER_FAILED: 'Error al editar responsable masivamente',
  BULK_EDIT_OWNER_SUCCESS: (count: number) =>
    `Responsable actualizado exitosamente para ${count} ${count === 1 ? 'riesgo' : 'riesgos'}`,
  BULK_EDIT_STATUS_FAILED: 'Error al editar estado masivamente',
  BULK_EDIT_STATUS_SUCCESS: (count: number) =>
    `Estado actualizado exitosamente para ${count} ${count === 1 ? 'riesgo' : 'riesgos'}`,
  BULK_DELETE_FAILED: 'Error al eliminar los riesgos',
  BULK_DELETE_SUCCESS: (count: number) => `${count} ${count === 1 ? 'riesgo eliminado' : 'riesgos eliminados'}`,
}

export const RISK_ASSETS = {
  servers_it_production: 'Servidores IT - Producción',
  servers_internal_development: 'Servidores Interno - Desarollo',
  source_code: 'Código Fuente',
  web_applications_external_production: 'Web Applications External Production',
  physical_servers: 'Servidores físicos',
  laptops_windows_linux_mac: 'Portátiles Windows, Linux, Mac',
  byod: 'BYOD',
  corporate_mobiles: 'Mobiles corporativos',
  corporate_tablets: 'Tablets corporativas',
  usb_drives: 'Unidades USB',
  user_peripherals: 'Periféricos utilizados por los usuarios',
  printers: 'Impresoras',
  offices_barcelona: 'Oficinas Barcelona',
  wireless_infrastructure: 'Infraestructura inalámbrica',
  subcontracted_office_facilities: 'Instalaciones de oficina subcontratadas',
  company_owned_office_facilities: 'Instalaciones de oficina propiedad de la empresa',
  saas_core: 'SaaS Core',
  saas_non_core: 'SaaS No Core',
  public_information: 'Información pública',
  confidential_information_technical_organizational: 'Información confidencial (técnica/organizativa)',
  confidential_information_personal_pii_phi: 'Información confidencial (personal - PII, PHI)',
  confidential_information_core: 'Información confidencial (core)',
  top_management: 'Alta Dirección',
  employees: 'Empleados',
  freelancers: 'Freenlances',
  administrators: 'Administradores',
}

export const NO_ASSET = 'Sin Activo'

export const ASSET_SELECTOR = {
  SEARCH_ASSETS: 'Buscar activos...',
  NO_ASSET_FOUND: 'No se encontró activo.',
}

export const AFFECTED_ASSETS_SELECTOR = {
  LABEL: 'Activos afectados',
  NO_ASSET: 'Sin activo',
  SEARCH: 'Buscar activos',
  NO_RESULTS: 'No se encontraron activos',
  LOAD_ERROR: 'No se pudieron cargar los activos',
  INVENTORY_COUNT_ONE: '1 activo en el inventario',
  INVENTORY_COUNT: '{count} activos en el inventario',
  MANAGE: 'Gestionar en el inventario de activos',
}
export const RISK_INTERESTED_PARTIES = {
  employees: 'Empleados',
  systems_administrators: 'Administradores de Sistemas',
  freelancers: 'Freelancers',
  clients: 'Clientes',
  prospects: 'Prospectos',
  suppliers: 'Proveedores',
  shareholders: 'Accionistas',
  regulatory_authorities: 'Autoridades Regulatorias',
  government: 'Gobierno',
  government_agencies_cybersecurity: 'Agencias Gubernamentales (Ciberseguridad)',
  society: 'Sociedad',
  competitors: 'Competencia',
  business_partners: 'Socios Comerciales',
}

export const NO_INTERESTED_PARTY = 'Sin Parte Interesada'

export const INTERESTED_PARTY_SELECTOR = {
  SEARCH_INTERESTED_PARTIES: 'Buscar partes interesadas...',
  NO_INTERESTED_PARTY_FOUND: 'No se encontró parte interesada.',
  ADD_PLACEHOLDER: 'Añadir una parte interesada',
  ADD: 'Añadir',
}

export const NO_CONTROL = 'Sin Control'

export const RISK_DASHBOARD = {
  TABS: {
    OVERVIEW: 'Vista General',
    RISK_LIST: 'Lista de Riesgos',
    TREATMENT_PLANS: 'Planes de tratamiento',
  },
  SUMMARY: {
    TOTAL_RISKS: 'Riesgos Identificados',
    HIGH_SEVERITY: 'Riesgos de Alta Severidad',
    TREATMENT: 'Tratamiento Previsto',
    MITIGATE: 'Mitigar',
    ACCEPT: 'Aceptar',
  },
  HEATMAP: {
    TITLE: 'Mapa de Calor de Riesgos',
    LIKELIHOOD: 'Probabilidad',
    IMPACT: 'Impacto',
    VIEW_ALL: 'Ver Todos los Riesgos →',
    INHERENT_RISK: 'Riesgo Inherente',
    RESIDUAL_RISK: 'Riesgo Residual',
    TOOLTIP: {
      RISK: 'Riesgo',
      RISKS: 'Riesgos',
      AT: 'en',
      MORE: 'más...',
    },
  },
  TOP_CLUSTERS: {
    TITLE: 'Clusters Más Afectados',
    NO_DATA: 'No hay datos de clusters disponibles',
  },
  EVOLUTION: {
    TITLE: 'Evolución del riesgo',
    DESCRIPTION: 'Riesgo residual en cada revisión',
    CURRENT: 'Actual',
    SINCE: 'Desde',
    NO_EARLIER_REVIEW: 'Sin revisión anterior',
    REVIEW: 'Revisión',
    NEW_RISKS: 'Riesgos nuevos',
    MORE: 'más',
  },
  CATALOGUE: {
    TOP_THREATS: 'Principales amenazas',
    TOP_VULNERABILITIES: 'Principales vulnerabilidades',
  },
}
